import React, { useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import { formatNumberToCurrency } from "../../../utils/formatter";
import styles from "./financeFutureEntries.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ALL_TYPES = "__ALL__";

const FinanceFutureEntries = ({ futureEntries = [], code }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [selectedBillType, setSelectedBillType] = useState(ALL_TYPES);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const aggregatedData = useMemo(() => {
    const map = new Map();
    (futureEntries || []).forEach((entry) => {
      const key = `${entry.yearMonth}__${entry.billType}`;
      const current = map.get(key);
      if (current) {
        current.quantity += Number(entry.quantity) || 0;
        current.totalValue += Number(entry.totalValue) || 0;
      } else {
        map.set(key, {
          yearMonth: entry.yearMonth,
          month: entry.month,
          billType: entry.billType,
          quantity: Number(entry.quantity) || 0,
          totalValue: Number(entry.totalValue) || 0
        });
      }
    });
    return [...map.values()].sort((a, b) =>
      a.yearMonth.localeCompare(b.yearMonth)
    );
  }, [futureEntries]);

  const billTypes = useMemo(() => {
    const set = new Set(aggregatedData.map((e) => e.billType));
    return [...set].sort();
  }, [aggregatedData]);

  const filteredData = useMemo(() => {
    if (selectedBillType === ALL_TYPES) return aggregatedData;
    return aggregatedData.filter((e) => e.billType === selectedBillType);
  }, [aggregatedData, selectedBillType]);

  const chartData = useMemo(() => {
    const monthMap = new Map();
    filteredData.forEach((entry) => {
      const current = monthMap.get(entry.yearMonth);
      if (current) {
        current.totalValue += entry.totalValue;
        current.quantity += entry.quantity;
      } else {
        monthMap.set(entry.yearMonth, {
          yearMonth: entry.yearMonth,
          month: entry.month,
          totalValue: entry.totalValue,
          quantity: entry.quantity
        });
      }
    });
    return [...monthMap.values()].sort((a, b) =>
      a.yearMonth.localeCompare(b.yearMonth)
    );
  }, [filteredData]);

  const data = {
    labels: chartData.map((m) => m.month),
    datasets: [
      {
        label: "Valor a vencer (R$)",
        data: chartData.map((m) => m.totalValue),
        backgroundColor: "#0962AD",
        borderRadius: 4
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          boxHeight: 6,
          boxWidth: 10,
          color: "#858585",
          font: { size: 14 }
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const idx = context.dataIndex;
            const item = chartData[idx];
            return [
              `${context.dataset.label}: ${formatNumberToCurrency(context.raw)}`,
              `Qtd. boletos: ${item?.quantity ?? 0}`
            ];
          }
        }
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        ticks: {
          callback: function (value) {
            return formatNumberToCurrency(value);
          }
        },
        title: {
          display: true,
          text: "Valor (R$)"
        }
      }
    }
  };

  const tableSortedData = useMemo(
    () => [...filteredData].sort((a, b) => a.yearMonth.localeCompare(b.yearMonth)),
    [filteredData]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(tableSortedData.length / itemsPerPage)
  );

  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return tableSortedData.slice(start, start + itemsPerPage);
  }, [tableSortedData, currentPage]);

  const emptyRows = Array(
    Math.max(0, itemsPerPage - pagedData.length)
  ).fill(null);

  const totals = useMemo(() => {
    return tableSortedData.reduce(
      (acc, row) => ({
        quantity: acc.quantity + (row.quantity || 0),
        totalValue: acc.totalValue + (row.totalValue || 0)
      }),
      { quantity: 0, totalValue: 0 }
    );
  }, [tableSortedData]);

  const handlePageClick = (page) => setCurrentPage(page);

  const handleBillTypeChange = (e) => {
    setSelectedBillType(e.target.value);
    setCurrentPage(1);
  };

  const hasData = tableSortedData.length > 0;

  return (
    <div className={styles.container}>
      {code && <span className={styles.code}>{code}</span>}

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Lançamentos Futuros</h2>
          <div style={{ position: "relative" }}>
            <img
              src={InfoIcon}
              alt="Informação"
              onClick={() => setShowPopover(!showPopover)}
              style={{
                width: "14px",
                height: "14px",
                cursor: "pointer",
                verticalAlign: "middle",
                display: "inline-block"
              }}
            />
            {showPopover && (
              <div className={styles.popover}>
                <p style={{ margin: 0, fontSize: "12px" }}>
                  Boletos com data de vencimento (DueDate) maior ou igual a
                  hoje + 1 dia e sem data de pagamento (ReceivedDate). A
                  data de vencimento é usada como referência. Use o filtro
                  por tipo de boleto para isolar Mensalidade, Acordo,
                  Serviços, etc.
                </p>
                <button
                  onClick={() => setShowPopover(false)}
                  className={styles.closeBtn}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.headerRight}>
          <label htmlFor="future-entries-billtype" className={styles.label}>
            Tipo de boleto:
          </label>
          <select
            id="future-entries-billtype"
            className={styles.select}
            value={selectedBillType}
            onChange={handleBillTypeChange}
          >
            <option value={ALL_TYPES}>Todos</option>
            {billTypes.map((bt) => (
              <option key={bt} value={bt}>
                {bt}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.chartWrapper}>
        {hasData ? (
          <Bar data={data} options={options} />
        ) : (
          <div className={styles.noChartData}>
            Sem lançamentos futuros para os filtros atuais.
          </div>
        )}
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Mês de Vencimento</th>
            <th>Tipo de Boleto</th>
            <th>Qtd. Boletos</th>
            <th>Valor a Vencer</th>
          </tr>
        </thead>
        <tbody>
          {hasData ? (
            <>
              {pagedData.map((row, idx) => (
                <tr key={`${row.yearMonth}-${row.billType}-${idx}`}>
                  <td>{row.month}</td>
                  <td>{row.billType}</td>
                  <td>{row.quantity}</td>
                  <td>{formatNumberToCurrency(row.totalValue)}</td>
                </tr>
              ))}
              {emptyRows.map((_, idx) => (
                <tr key={`empty-${idx}`}>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
              ))}
            </>
          ) : (
            <tr>
              <td colSpan="4" className={styles.noData}>
                Nenhum lançamento futuro encontrado
              </td>
            </tr>
          )}
        </tbody>
        {hasData && (
          <tfoot>
            <tr>
              <td colSpan="2" className={styles.totalLabel}>
                Total
              </td>
              <td className={styles.totalCell}>{totals.quantity}</td>
              <td className={styles.totalCell}>
                {formatNumberToCurrency(totals.totalValue)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={page === currentPage ? styles.activePage : ""}
              onClick={() => handlePageClick(page)}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FinanceFutureEntries;
